import { Dispatch, SetStateAction } from 'react';
import { useFormik } from 'formik';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { ADD, ADD_PERMISSION, CANCEL, PERMISSION_DESC, PERMISSION_NAME } from '../../../../../constants/strings';
import { useMutation, useQueryClient } from 'react-query';
import HttpServiceHelper from '../../../../../helpers/httpServiceHelper';
import { useTranslation } from '../../../../../hooks/useTranslation';
import { _PermissionGroup, _ToastMessageProps } from '../../../../../constants/staticTypes';
import { AlertVariant } from '../../../../../constants/constants';
import { ORG_ADMIN_PERMISSION_GROUP_LIST, ORG_STAFF_PERMISSION_GROUP_OPTIONS } from '../../../../../constants/queryKeys';
import { AddPermissionFormSchema, initialAddAdminPermissionFormData } from '../../../../../helpers/permissionHelper';
import { JTranslation } from '../../../../../helpers/jTranslationHelper';
import MlxDialog from '../../../../../components/common/MlxDialog/MlxDialog';
import MlxButton from '../../../../../components/common/MlxButton/MlxButton';
import { addOrgPermission } from '../../../../../helpers/orgPermissionHelper';

type _AddOrgPermissionProps = {
    visible: boolean;
    setVisible: Dispatch<SetStateAction<boolean>>;
    setToastMessage: React.Dispatch<SetStateAction<_ToastMessageProps>>;
    setActiveGroupPermission: React.Dispatch<SetStateAction<_PermissionGroup | null>>;
};

const languageTexts = {
    ADD: ADD,
    CANCEL: CANCEL,
};

function AddOrgAdminPermission({ visible, setVisible, setToastMessage, setActiveGroupPermission }: Readonly<_AddOrgPermissionProps>) {
    const queryClient = useQueryClient();
    const addPermissionMutation = useMutation(HttpServiceHelper);
    const translatedText = useTranslation(languageTexts, 'capitalize');

    const onSuccess = (data: any) => {
        setActiveGroupPermission((data?.data as _PermissionGroup) ?? null);
        setToastMessage({
            message: data?.message ?? '',
            variant: AlertVariant.SUCCESS,
            show: true,
        });
        queryClient.refetchQueries([ORG_ADMIN_PERMISSION_GROUP_LIST]);
        queryClient.refetchQueries([ORG_STAFF_PERMISSION_GROUP_OPTIONS]); // Add Staff sidebar picker
        resetForm();
    };

    const onError = (error: string, variant: string) => {
        setToastMessage({
            message: error,
            variant,
            show: true,
        });
        resetForm();
    };

    const resetForm = () => {
        addOrgPermissionForm.resetForm();
        setVisible(false);
    };

    const addOrgPermissionForm = useFormik({
        enableReinitialize: true,
        initialValues: initialAddAdminPermissionFormData,
        validationSchema: AddPermissionFormSchema,
        onSubmit: (data) => {
            let payload = data;
            addOrgPermission(addPermissionMutation, payload, onSuccess, onError);
        },
    });

    const header = () => {
        return <JTranslation text={ADD_PERMISSION} type="html" typeCase="pascal" />;
    };

    return (
        <MlxDialog visible={visible} onHide={() => setVisible(false)} header={header}>
            <form onSubmit={addOrgPermissionForm.handleSubmit}>
                <div className="mb-3 d-flex flex-column">
                    <label className="mb-1" htmlFor="permissionRoleName">
                        <JTranslation text={PERMISSION_NAME} typeCase="pascal" /> <span className="text-danger">*</span>
                    </label>
                    <InputText
                        className={`custom-input-text p-inputtext-sm ${addOrgPermissionForm?.touched?.permissionRoleName && addOrgPermissionForm?.errors?.permissionRoleName ? 'border-danger' : ''}`}
                        id="permissionRoleName"
                        value={addOrgPermissionForm?.values?.permissionRoleName}
                        onChange={(event) => {
                            addOrgPermissionForm.setFieldTouched('permissionRoleName', true);
                            addOrgPermissionForm.setFieldValue('permissionRoleName', event.target.value, true);
                        }}
                        autoComplete="off"
                    />
                </div>
                <div className="mb-3 d-flex flex-column">
                    <label className="mb-1" htmlFor="permissionRoleDescription">
                        <JTranslation text={PERMISSION_DESC} typeCase="pascal" />
                    </label>
                    <InputTextarea
                        rows={3}
                        className={`${addOrgPermissionForm?.touched?.permissionRoleDescription && addOrgPermissionForm?.errors?.permissionRoleDescription ? 'border-danger' : ''}`}
                        id="permissionRoleDescription"
                        value={addOrgPermissionForm?.values?.permissionRoleDescription}
                        onChange={(event) => {
                            addOrgPermissionForm.setFieldTouched('permissionRoleDescription', true);
                            addOrgPermissionForm.setFieldValue('permissionRoleDescription', event.target.value, true);
                        }}
                    />
                </div>
            </form>
            <div className="mt-4 d-flex justify-content-end">
                <MlxButton
                    label={translatedText.CANCEL}
                    onClick={() => {
                        addOrgPermissionForm.resetForm();
                        setVisible(false);
                    }}
                    icon="ri-close-large-line"
                    iconPos="right"
                    size="small"
                    outlined
                    type="button"
                />
                <MlxButton
                    type="button"
                    className="ms-2"
                    label={translatedText.ADD}
                    icon={'ri-add-line'}
                    iconPos="right"
                    size="small"
                    onClick={() => addOrgPermissionForm.submitForm()}
                    loading={addPermissionMutation?.isLoading}
                    disabled={addPermissionMutation?.isLoading}
                />
            </div>
        </MlxDialog>
    );
}

export default AddOrgAdminPermission;
